import React, { useState, useMemo } from 'react';
import { ReactSortable } from 'react-sortablejs';
import { IDraggableVideoMediafile, IVideoStreamEmenuMediafile, IVideoStreamItem } from '../types';
import { assignOrderByIndex, sortItemsByOrder } from '../utils';
import { DraggableListItem } from './DraggableListItem';

import styles from './index.module.scss';
import PreviewItem from './PreviewItem';

export interface DraggableItemListType {
  itemList?: IDraggableVideoMediafile[];
  emenuMediafiles?: IVideoStreamEmenuMediafile[];
  updateItems?: (items: Array<IDraggableVideoMediafile>) => void;
  updateEmenuItems?: (items: IVideoStreamEmenuMediafile[]) => void;
  onSetDuration: (id: string, duration: number) => void;
  onRemoveMediafile: (id: string) => void;
  onCopyMediafile: (id: string) => void;
  isBlockedDurationEdit?: boolean;
}

const DraggableVideoMediafiesList: React.FC<DraggableItemListType> = ({
  itemList,
  emenuMediafiles,
  updateItems,
  updateEmenuItems,
  onSetDuration,
  onRemoveMediafile,
  onCopyMediafile,
  isBlockedDurationEdit,
}) => {
  const [localEmenu, setLocalEmenu] = useState<IVideoStreamEmenuMediafile[]>(() => emenuMediafiles ?? []);

  const mergedList = useMemo<IVideoStreamItem[]>(() => {
    const videoItems: IVideoStreamItem[] = (itemList ?? []).map((it) => ({
      ...it,
      itemType: 'video' as const,
    }));
    const emenuItems: IVideoStreamItem[] = localEmenu.map((it) => ({
      ...it,
      itemType: 'emenu' as const,
    }));
    return sortItemsByOrder([...videoItems, ...emenuItems]);
  }, [itemList, localEmenu]);
  const isDragHandleVisible = mergedList.length > 1;

  const handleSetList = (newList: IVideoStreamItem[]) => {
    const reordered = assignOrderByIndex(newList);

    const emenuItems = reordered
      .filter((it) => it.itemType === 'emenu')
      .map(({ itemType: _t, ...rest }) => rest as IVideoStreamEmenuMediafile);
    setLocalEmenu(emenuItems);
    updateEmenuItems?.(emenuItems);

    if (!updateItems) return;
    const videoItems = reordered
      .filter((it) => it.itemType === 'video')
      .map(({ itemType: _t, ...rest }) => rest as IDraggableVideoMediafile);
    updateItems(videoItems);
  };

  return (
    <ReactSortable<IVideoStreamItem>
      list={mergedList}
      handle=".draggable"
      disabled={!isDragHandleVisible}
      setList={handleSetList}>
      {mergedList.map((item) => (
        <DraggableListItem
          key={item.id}
          dragHandleTestId={`drag-handle_${item.itemType}_mediafile_${item.id}`}
          isDraggable={isDragHandleVisible}>
          <PreviewItem
            className={styles.content}
            onSetDuration={onSetDuration}
            file={item}
            onRemoveMediafile={onRemoveMediafile}
            onCopyMediafile={onCopyMediafile}
            isBlockedDurationEdit={isBlockedDurationEdit}
          />
        </DraggableListItem>
      ))}
    </ReactSortable>
  );
};

export default DraggableVideoMediafiesList;
